import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { ArrowRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCourseGlossaryStore } from "@/stores/courseGlossaryStore";
import { splitAliases } from "@/lib/glossaryFilter";

interface GlossaryTermMarkProps {
  termId: string;
  children?: ReactNode;
}

/**
 * Inline glossary term: a dotted-underlined word that opens a small popover with
 * the term's explanation. Rendered for the first occurrence of each term per
 * markdown block (see rehypeGlossary / GlossaryHighlightProvider).
 */
export const GlossaryTermMark = ({
  termId,
  children,
}: GlossaryTermMarkProps) => {
  const [open, setOpen] = useState(false);

  const term = useCourseGlossaryStore((state) =>
    state.terms.find((t) => t.id === termId)
  );
  const openGlossary = useCourseGlossaryStore((state) => state.openGlossary);

  // Term no longer known (e.g. glossary changed): render plain text.
  if (!term) return <>{children}</>;

  const aliasList = splitAliases(term.aliases);

  const handleMore = () => {
    setOpen(false);
    openGlossary(term.id);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Erläuterung: ${term.name}`}
          className="cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-primary/50 decoration-dotted decoration-2 underline-offset-[3px] transition-colors hover:decoration-primary focus-visible:outline-none focus-visible:decoration-primary"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] space-y-2"
      >
        <p className="text-sm font-semibold text-primary">{term.name}</p>
        {aliasList.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Auch: {aliasList.join(", ")}
          </p>
        )}
        {term.description && (
          <div className="prose prose-sm max-w-none text-sm text-muted-foreground">
            <ReactMarkdown>{term.description}</ReactMarkdown>
          </div>
        )}
        <button
          type="button"
          onClick={handleMore}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
        >
          Mehr im Glossar
          <ArrowRight className="size-3.5" />
        </button>
      </PopoverContent>
    </Popover>
  );
};
